import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth, GoogleAuthProvider, signInWithPopup,
  RecaptchaVerifier, signInWithPhoneNumber,
  sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink,
  setPersistence, browserLocalPersistence,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

export function fbApp() {
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
}

export function fbAuth() {
  const auth = getAuth(fbApp());
  setPersistence(auth, browserLocalPersistence).catch(() => {});
  return auth;
}

const EMAIL_KEY = "pp_email_for_signin";
const emailLinkSettings = () => ({
  url: `${window.location.origin}/login`,
  handleCodeInApp: true,
});

// --- Google ---
export async function googleSignIn() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  const res = await signInWithPopup(fbAuth(), provider);
  return res.user.getIdToken();
}

// --- Phone SMS ---
export function makeRecaptcha(containerId) {
  return new RecaptchaVerifier(fbAuth(), containerId, { size: "invisible" });
}
export async function phoneSendCode(phoneNumber, verifier) {
  return signInWithPhoneNumber(fbAuth(), phoneNumber, verifier);
}
export async function phoneConfirm(confirmationResult, code) {
  const res = await confirmationResult.confirm(code);
  return res.user.getIdToken();
}

// --- Email magic link ---
// Note: the sign-in email is kept in sessionStorage (tab-scoped, cleared on close),
// not localStorage, to avoid persisting it where XSS could read it long-term.
export async function emailSendLink(email) {
  await sendSignInLinkToEmail(fbAuth(), email, emailLinkSettings());
  window.sessionStorage.setItem(EMAIL_KEY, email);
}
export function isEmailLink(url = window.location.href) {
  return isSignInWithEmailLink(fbAuth(), url);
}
export function storedEmail() {
  return window.sessionStorage.getItem(EMAIL_KEY) || "";
}
export async function emailCompleteSignIn(email, url = window.location.href) {
  const res = await signInWithEmailLink(fbAuth(), email, url);
  window.sessionStorage.removeItem(EMAIL_KEY);
  return res.user.getIdToken();
}

export function humanizeFirebaseError(err) {
  const code = err?.code || "";
  const map = {
    "auth/popup-closed-by-user": "Sign-in window closed before finishing.",
    "auth/cancelled-popup-request": "Only one sign-in window at a time.",
    "auth/popup-blocked": "Your browser blocked the popup. Allow popups and retry.",
    "auth/invalid-phone-number": "That phone number doesn't look right.",
    "auth/invalid-verification-code": "That code is incorrect. Try again.",
    "auth/too-many-requests": "Too many attempts. Please wait a bit and retry.",
    "auth/invalid-email": "That email address isn't valid.",
    "auth/unauthorized-domain": "This domain isn't authorized in Firebase yet.",
    "auth/network-request-failed": "Network error. Check your connection.",
    "auth/user-disabled": "This account has been disabled.",
  };
  return map[code] || err?.message?.replace("Firebase: ", "") || "Sign-in failed. Please try again.";
}
